import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/** One tab. `id` names both the tab and the slot its panel content goes in. */
export interface BnTab {
  /** Stable key. Also the `slot` name of the panel content. */
  id: string;
  label: string;
  /**
   * Renders the tab unselectable and skips it in arrow navigation. Use it when
   * the instance's build does not expose the surface behind the tab, rather than
   * offering a tab that leads to a 501.
   */
  disabled?: boolean;
}

/** Detail of bn-tabs' `change` event: the id of the newly selected tab. */
export interface BnTabsChangeDetail {
  id: string;
}

let uid = 0;

/**
 * bn-tabs is the kit's tab list plus its panel. It implements the APG tabs
 * pattern with a roving tabindex — only the selected tab is in the page's tab
 * order, and Left/Right/Home/End move between tabs — and automatic activation,
 * so moving focus selects. Panel content is slotted by tab id, which keeps the
 * `role="tab"` / `role="tabpanel"` / `aria-controls` wiring inside the component
 * instead of in every page that uses it.
 *
 * Selection is mirrored on `active` and reported by a composed `change`
 * CustomEvent, so a page can also drive it from the URL.
 *
 * Properties:
 * - `tabs`   — `BnTab[]`, in display order; property-only
 * - `active` — id of the selected tab; falls back to the first enabled tab
 * - `label`  — accessible name for the tab list
 *
 * Slots: one per tab, named by the tab's `id`. Only the selected tab's slot is
 * rendered, so an unselected panel's content is never displayed.
 *
 * Events:
 * - `change` — `CustomEvent<BnTabsChangeDetail>`, bubbles and composed. Not
 *   fired when the already-selected tab is clicked.
 *
 *   <bn-tabs
 *     label="Instance console"
 *     .tabs=${[
 *       { id: "overview", label: "Overview" },
 *       { id: "policies", label: "Policies" },
 *       { id: "logs", label: "Logs", disabled: !caps.logs },
 *     ]}
 *     .active=${tab}
 *     @change=${(e: CustomEvent<BnTabsChangeDetail>) => (tab = e.detail.id)}
 *   >
 *     <page-instance-overview slot="overview"></page-instance-overview>
 *     <page-instance-policies slot="policies"></page-instance-policies>
 *     <page-instance-logs slot="logs"></page-instance-logs>
 *   </bn-tabs>
 */
@customElement("bn-tabs")
export class BnTabs extends LitElement {
  /** The tabs, in display order. Set as a property, never an attribute. */
  @property({ attribute: false }) tabs: BnTab[] = [];
  /** Id of the selected tab. Falls back to the first enabled tab. */
  @property() active = "";
  /** Accessible name for the tab list, e.g. "Policy editor sections". */
  @property() label = "";

  private readonly base = `bn-tabs-${++uid}`;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
    }
    .list {
      display: flex;
      gap: var(--bino-space-xs);
      border-bottom: 1px solid var(--bino-border);
      overflow-x: auto;
      scrollbar-width: thin;
    }
    .tab {
      flex: none;
      font-family: inherit;
      font-size: var(--bino-font-size-md);
      font-weight: 500;
      line-height: 1;
      color: var(--bino-text-secondary);
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      border-radius: var(--bino-radius-sm) var(--bino-radius-sm) 0 0;
      padding: var(--bino-space-sm) var(--bino-space-md);
      margin-bottom: -1px;
      cursor: pointer;
      white-space: nowrap;
      transition:
        color var(--bino-transition-fast),
        background var(--bino-transition-fast),
        border-color var(--bino-transition-fast);
    }
    .tab:hover:not(:disabled) {
      color: var(--bino-text);
      background: var(--bino-surface-hover);
    }
    .tab:focus-visible {
      outline: none;
      /* An INSET ring, unlike everywhere else in the kit: .list is an
         overflow:auto scroller (the tab strip pans rather than wrapping) and a
         tab fills its box exactly, so an outer 3px ring is clipped on all four
         sides and the focus indicator disappears entirely. bn-code rings its
         read-only <pre> the same way, for the same reason. */
      box-shadow: inset 0 0 0 2px var(--bino-focus-ring);
    }
    .tab[aria-selected="true"] {
      color: var(--bino-active-text);
      border-bottom-color: var(--bino-primary);
    }
    .tab:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .panel {
      padding-top: var(--bino-space-md);
    }
    .panel:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
      border-radius: var(--bino-radius-sm);
    }
  `;

  /** The effective selection: `active` when it names a tab, else the first enabled one. */
  private get current(): string {
    if (this.tabs.some((t) => t.id === this.active)) return this.active;
    return this.tabs.find((t) => !t.disabled)?.id ?? "";
  }

  private tabId(index: number): string {
    return `${this.base}-tab-${index}`;
  }

  private panelId(index: number): string {
    return `${this.base}-panel-${index}`;
  }

  private select(id: string, moveFocus: boolean) {
    if (id === "" || id === this.current) return;
    this.active = id;
    this.dispatchEvent(
      new CustomEvent<BnTabsChangeDetail>("change", {
        detail: { id },
        bubbles: true,
        composed: true,
      }),
    );
    if (!moveFocus) return;
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector<HTMLButtonElement>('.tab[aria-selected="true"]')?.focus();
    });
  }

  private onKeydown(e: KeyboardEvent) {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return;
    const usable = this.tabs.filter((t) => !t.disabled);
    if (usable.length === 0) return;
    e.preventDefault();

    const at = usable.findIndex((t) => t.id === this.current);
    let next: number;
    if (e.key === "Home") next = 0;
    else if (e.key === "End") next = usable.length - 1;
    else if (at < 0) next = 0;
    else if (e.key === "ArrowRight") next = (at + 1) % usable.length;
    else next = (at - 1 + usable.length) % usable.length;

    const tab = usable[next];
    if (tab) this.select(tab.id, true);
  }

  override render() {
    const current = this.current;
    const activeIndex = this.tabs.findIndex((t) => t.id === current);

    return html`
      <div class="list" role="tablist" aria-label=${this.label || nothing} @keydown=${this.onKeydown}>
        ${this.tabs.map((t, i) => {
          const on = t.id === current;
          return html`
            <button
              class="tab"
              type="button"
              role="tab"
              id=${this.tabId(i)}
              aria-selected=${on ? "true" : "false"}
              aria-controls=${on ? this.panelId(i) : nothing}
              tabindex=${on ? 0 : -1}
              ?disabled=${t.disabled}
              @click=${() => this.select(t.id, false)}
            >
              ${t.label}
            </button>
          `;
        })}
      </div>
      <div
        class="panel"
        role="tabpanel"
        id=${activeIndex >= 0 ? this.panelId(activeIndex) : nothing}
        aria-labelledby=${activeIndex >= 0 ? this.tabId(activeIndex) : nothing}
        tabindex="0"
      >
        ${current ? html`<slot name=${current}></slot>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-tabs": BnTabs;
  }
}
